import {
  addRowAfter,
  addRowBefore,
  backspace,
  deleteRow,
  makeKeyMapWithCommon,
  Keymap,
  tooltip,
  moveRowUp,
  moveRowDown,
} from '@atlaskit/editor-common/keymaps';
import ArrowDownIcon from '@atlaskit/icon/glyph/arrow-down';
import ArrowUpIcon from '@atlaskit/icon/glyph/arrow-up';
import CopyIcon from '@atlaskit/icon/glyph/copy';
import CrossCircleIcon from '@atlaskit/icon/glyph/cross-circle';
import LinkIcon from '@atlaskit/icon/glyph/link';
import TrashIcon from '@atlaskit/icon/glyph/trash';
import { TriggerProps } from '@atlaskit/popup';
import { Flex, Inline, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import {
  getDatabaseContainerType,
  SendDatabaseDeleteEntryEventSources,
  sendDeleteEntryEvent,
  useAnalytics,
} from '@databases/analytics';
import ContextualMenu, {
  MenuData,
  MenuItem,
  useContextualMenu,
} from '@databases/contextual-menu';
import { copyHtmlToClipboard } from '@databases/export';
import { useFlaggingCallback } from '@databases/flags';
import {
  ActiveVerticalDragHandlerIcon,
  CollapseIcon,
  ExpandIcon,
  InsertRowAboveIcon,
  InsertRowBelowIcon,
  VerticalDragHandlerIcon,
} from '@databases/icons';
import { messages } from '@databases/intl/messages';
import usePanel from '@databases/panel/usePanel';
import {
  HoverProps,
  isModifierKeyPressed,
  KeyboardShortcut,
  useComponent,
  useKeyboardShortcut,
} from '@databases/react';
import { DefaultRootState, defaultStoreUtils } from '@databases/redux';
import { setHoverDeleteButton } from '@databases/redux/cellSelections/actions';
import {
  selectHasEntryAfterSelection,
  selectHasEntryBeforeSelection,
  selectIsEntireEntrySelected,
  isEntrySelected,
  selectIsHoverDeleteEntriesButton,
  selectSelectedEntireEntryIds,
  selectSelectedEntriesCount,
} from '@databases/redux/cellSelections/selectors';
import {
  doClearEntireEntries,
  doDeleteEntries,
  doDuplicateEntireEntries,
  doInsertEntries,
  doMoveEntries,
  doSelectNewEntries,
} from '@databases/redux/contextual-menu/actions';
import { DatabaseViewSettings } from '@databases/types/database/view';
import { selectBaseUrl } from '@databases/utils/redux/context/selectors';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { getDatabasePath } from '@databases/utils/urls';
import React, { FunctionComponent, useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { TABLE_CLASSNAMES } from '../../../../constants';
import {
  ADD_ABOVE_SHORTCUT,
  ADD_BELOW_SHORTCUT,
  CLEAR_SHORTCUT,
  DELETE_SHORTCUT,
  DUPLICATE_SHORTCUT,
  MOVE_DOWN_SHORTCUT,
  MOVE_UP_SHORTCUT,
} from '../../../../shortcuts';
import { IGNORED_TABLE_ELEMENT_CLASS } from '../../TableResizer';

import { DragHandlerWrapper, RowSelectButtonContainer } from './styles';
import { Position } from './type';

const elementBeforeIconWrapper = xcss({
  marginRight: 'space.negative.075',
});

const elementAfterShortCutWrapper = xcss({
  backgroundColor: 'color.background.neutral',
  color: 'color.text.subtlest',
  borderRadius: 'border.radius.050',
  padding: 'space.050',
  lineHeight: 1,
  fontSize: 11.67,
  alignSelf: 'flex-end',
});

enum SELECT_ENTRY_MENU_ACTIONS {
  EXPAND_ENTRY = 'expand_entry',
  COLLAPSE_ENTRY = 'collapse_entry',
  COPY_ENTRY_LINK = 'copy_entry_link',
  ADD_ENTRY_ABOVE = 'add_entry_above',
  ADD_ENTRY_BELOW = 'add_entry_below',
  DUPLICATE_ENTRIES = 'duplicate_entries',
  CLEAR_CELLS = 'clear_cells',
  DELETE_ENTRIES = 'delete_entries',
  MOVE_ENTRIES_UP = 'move_entries_up',
  MOVE_ENTRIES_DOWN = 'move_entries_down',
}

const SELECT_ENTRY_SHORTCUTS: Record<
  SELECT_ENTRY_MENU_ACTIONS,
  { keyboardShortCut: KeyboardShortcut[]; keyMap: Keymap } | undefined
> = {
  [SELECT_ENTRY_MENU_ACTIONS.EXPAND_ENTRY]: undefined,
  [SELECT_ENTRY_MENU_ACTIONS.COLLAPSE_ENTRY]: undefined,
  [SELECT_ENTRY_MENU_ACTIONS.COPY_ENTRY_LINK]: undefined,
  [SELECT_ENTRY_MENU_ACTIONS.ADD_ENTRY_ABOVE]: {
    keyMap: addRowBefore,
    keyboardShortCut: ADD_ABOVE_SHORTCUT,
  },
  [SELECT_ENTRY_MENU_ACTIONS.ADD_ENTRY_BELOW]: {
    keyMap: addRowAfter,
    keyboardShortCut: ADD_BELOW_SHORTCUT,
  },
  [SELECT_ENTRY_MENU_ACTIONS.DUPLICATE_ENTRIES]: {
    keyMap: makeKeyMapWithCommon('Copy', 'Mod-d'),
    keyboardShortCut: DUPLICATE_SHORTCUT,
  },
  [SELECT_ENTRY_MENU_ACTIONS.CLEAR_CELLS]: {
    keyMap: backspace,
    keyboardShortCut: CLEAR_SHORTCUT,
  },
  [SELECT_ENTRY_MENU_ACTIONS.DELETE_ENTRIES]: {
    keyMap: deleteRow,
    keyboardShortCut: DELETE_SHORTCUT,
  },
  [SELECT_ENTRY_MENU_ACTIONS.MOVE_ENTRIES_UP]: {
    keyMap: moveRowUp,
    keyboardShortCut: MOVE_UP_SHORTCUT,
  },
  [SELECT_ENTRY_MENU_ACTIONS.MOVE_ENTRIES_DOWN]: {
    keyMap: moveRowDown,
    keyboardShortCut: MOVE_DOWN_SHORTCUT,
  },
};

type SelectEntryMenuProps = {
  contentId: string;
  scope: string;
  entryId: string;
  viewSettings?: DatabaseViewSettings;
  currentViewId?: string;
  hoverProps?: HoverProps;
  handleSelectEntry: (e: React.MouseEvent) => void;
  canEditContent: boolean;
  trigger?: (triggerProps: TriggerProps) => React.ReactNode;
};

const SelectEntryMenu: FunctionComponent<SelectEntryMenuProps> = ({
  contentId,
  scope,
  entryId,
  currentViewId,
  viewSettings,
  hoverProps,
  handleSelectEntry,
  canEditContent,
  trigger,
}) => {
  const [
    canDeleteEntry,
    canDuplicateEntry,
    canInsertEntry,
    canMoveUp,
    canMoveDown,
    isSelected,
    oneSelectedEntry,
    canCopyValue,
    selectedEntriesCount,
    baseUrl,
    databasePath,
    isHoverDeleteEntriesButton,
  ] = defaultStoreUtils.useEqualSelector((state: DefaultRootState) => {
    const database = selectDatabaseByContentIdRequired(state, {
      contentId,
    });
    const isSelected = selectIsEntireEntrySelected(state, {
      entryId,
    });
    const selectedEntireEntryIds = isSelected
      ? selectSelectedEntireEntryIds(state, {
          contentId,
          scope,
          viewSettings,
        })
      : [];
    const oneSelectedEntry =
      selectedEntireEntryIds.length > 1 ? null : selectedEntireEntryIds[0];

    const selectedEntriesCount = isSelected
      ? selectSelectedEntriesCount(state, {
          contentId,
          scope,
          viewSettings,
        })
      : 0;
    const hasEntryBeforeSelection =
      isSelected &&
      selectHasEntryBeforeSelection(state, {
        contentId,
        scope,
        viewSettings,
      });
    const hasEntryAfterSelection =
      isSelected &&
      selectHasEntryAfterSelection(state, {
        contentId,
        scope,
        viewSettings,
      });
    const isHoverDeleteEntriesButton =
      isSelected && selectIsHoverDeleteEntriesButton(state);

    const canInsertEntry = canEditContent && selectedEntriesCount > 0;
    const canDuplicateEntry = canEditContent && selectedEntriesCount > 0;
    const canDeleteEntry = canEditContent && selectedEntriesCount > 0;
    const canCopyValue = canEditContent && selectedEntriesCount === 1;
    const canMoveUp =
      canEditContent && selectedEntriesCount > 0 && hasEntryBeforeSelection;
    const canMoveDown =
      canEditContent && selectedEntriesCount > 0 && hasEntryAfterSelection;
    return [
      canDeleteEntry,
      canDuplicateEntry,
      canInsertEntry,
      canMoveUp,
      canMoveDown,
      isSelected,
      oneSelectedEntry,
      canCopyValue,
      selectedEntriesCount,
      selectBaseUrl(state),
      getDatabasePath(database),
      isHoverDeleteEntriesButton,
    ] as const;
  });

  const { formatMessage } = useIntl();
  const store = defaultStoreUtils.useStore();
  const dispatch = defaultStoreUtils.useDispatch();
  const client = useAnalytics();

  const { showPanel, hidePanel, isPanelVisible, getData } = usePanel();
  const { entryId: panelEntryId } = getData<'EntryViewPanel'>() || {};

  const handleCollapseEntry = useFlaggingCallback(() => {
    hidePanel();
  }, [hidePanel]);

  const handleExpandEntry = useFlaggingCallback(() => {
    showPanel('EntryViewPanel', {
      entryId,
      isReadonly: !canEditContent,
    });
  }, [canEditContent, entryId, showPanel]);

  const menuData: MenuData = {
    type: 'entry',
    entryId,
  };
  const { isMenuOpen, toggleOpenMenu } = useContextualMenu(menuData);
  const prevIsSelected = useRef(isSelected);
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      toggleOpenMenu();
      if (!isSelected) {
        handleSelectEntry(e);
        prevIsSelected.current = true;
      } else {
        prevIsSelected.current = false;
      }
    },
    [handleSelectEntry, isSelected, toggleOpenMenu]
  );

  const handleMouseUp = useFlaggingCallback(
    (e: React.MouseEvent) => {
      const isMultiSelectOn = isModifierKeyPressed(e, ['shiftKey']);
      if (isSelected && !prevIsSelected.current && !isMenuOpen) {
        // remove selection if the user clicks on the select button again
        handleSelectEntry(e);
      } else if (!isMultiSelectOn && isPanelVisible('EntryViewPanel')) {
        // open the entry dialog only if the dialog has already been opened from another entry
        handleExpandEntry();
      }
    },
    [handleExpandEntry, handleSelectEntry, isMenuOpen, isPanelVisible, isSelected]
  );

  const handleRightClick = (e: React.MouseEvent) => e.preventDefault();

  const defaultTrigger = useComponent(
    ({ ref, ...triggerProps }: TriggerProps) => (
      <RowSelectButtonContainer
        className={IGNORED_TABLE_ELEMENT_CLASS}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onContextMenu={handleRightClick}
        {...triggerProps}
        {...hoverProps}
        ref={ref}
      >
        <DragHandlerWrapper
          className={TABLE_CLASSNAMES.SELECT_BUTTON}
          isActive={isMenuOpen && isSelected}
          isDeletionActive={isHoverDeleteEntriesButton}
        >
          <div className={TABLE_CLASSNAMES.ACTIVE_DRAG_HANDLER_ICON}>
            <ActiveVerticalDragHandlerIcon
              label={TABLE_CLASSNAMES.ACTIVE_DRAG_HANDLER_ICON}
            />
          </div>
          <div className={TABLE_CLASSNAMES.DRAG_HANDLER_ICON}>
            <VerticalDragHandlerIcon label={TABLE_CLASSNAMES.DRAG_HANDLER_ICON} />
          </div>
        </DragHandlerWrapper>
      </RowSelectButtonContainer>
    ),
    [
      handleMouseDown,
      handleMouseUp,
      hoverProps,
      isMenuOpen,
      isSelected,
      isHoverDeleteEntriesButton,
    ]
  );

  const triggerComp = trigger ?? defaultTrigger;

  const [isCopied, setIsCopied] = useState(false);
  const handleCopyEntryLink = useFlaggingCallback(() => {
    const url = new URL(baseUrl + databasePath);
    url.searchParams.set('contentId', contentId);
    url.searchParams.set('entryId', entryId);
    if (currentViewId) {
      url.searchParams.set('savedViewId', currentViewId);
    }

    copyHtmlToClipboard(url.toString());
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 3000);
  }, [baseUrl, databasePath, contentId, entryId, currentViewId]);

  const handleInsertEntries = useFlaggingCallback(
    async (position: Position) => {
      await dispatch(
        doSelectNewEntries(contentId, () =>
          dispatch(
            doInsertEntries(contentId, scope, viewSettings, position === Position.BEFORE)
          )
        )
      );
      await client.sendInsertRowEvent({
        contentId: contentId,
        databaseContainer: getDatabaseContainerType(),
      });
    },
    [client, contentId, dispatch, scope, viewSettings]
  );

  const handleDuplicateEntries = useFlaggingCallback(() => {
    dispatch(
      doSelectNewEntries(contentId, () =>
        dispatch(doDuplicateEntireEntries(contentId, scope, viewSettings))
      )
    );
  }, [dispatch, contentId, scope, viewSettings]);

  const handleClearEntries = useFlaggingCallback(() => {
    dispatch(doClearEntireEntries(contentId, scope, viewSettings));
  }, [contentId, dispatch, scope, viewSettings]);

  const handleDeleteEntries = useFlaggingCallback(() => {
    if (isEntrySelected(store.getState(), contentId, scope, viewSettings, panelEntryId)) {
      hidePanel();
    }

    dispatch(doDeleteEntries(contentId, scope, viewSettings));
    dispatch(setHoverDeleteButton({ value: null }));
    void sendDeleteEntryEvent(
      client,
      SendDatabaseDeleteEntryEventSources.ENTRY_CONTEXTUAL_MENU,
      store.getState(),
      contentId
    );
  }, [store, contentId, scope, viewSettings, panelEntryId, dispatch, client, hidePanel]);

  const handleMoveEntries = useCallback(
    (position: Position) => {
      dispatch(
        doMoveEntries(contentId, scope, viewSettings, position === Position.BEFORE)
      );
    },
    [contentId, dispatch, scope, viewSettings]
  );

  const expandEntryMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_ENTRY_MENU_ACTIONS.EXPAND_ENTRY,
      content: formatMessage(messages['entry-actions.expand']),
      onClick: handleExpandEntry,
      closeOnClick: true,
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <ExpandIcon label="" size="medium" />
        </Flex>
      ),
    }),
    [formatMessage, handleExpandEntry]
  );

  const collapseEntryMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_ENTRY_MENU_ACTIONS.COLLAPSE_ENTRY,
      content: formatMessage(messages['entry-actions.collapse']),
      onClick: handleCollapseEntry,
      closeOnClick: true,
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <CollapseIcon label="" size="medium" />
        </Flex>
      ),
    }),
    [formatMessage, handleCollapseEntry]
  );

  const copyEntryLinkMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_ENTRY_MENU_ACTIONS.COPY_ENTRY_LINK,
      content: formatMessage(messages['entry-actions.copy-link']),
      onClick: handleCopyEntryLink,
      tooltipDescription: isCopied
        ? formatMessage(messages['copy-link.tooltip'])
        : undefined,
      tooltipPosition: 'right',
      elemBefore: (
        <Inline
          spread="space-between"
          alignBlock="center"
          xcss={elementBeforeIconWrapper}
        >
          <LinkIcon label="" size="medium" />
        </Inline>
      ),
    }),
    [formatMessage, handleCopyEntryLink, isCopied]
  );

  const addEntryAboveMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_ENTRY_MENU_ACTIONS.ADD_ENTRY_ABOVE,
      content: formatMessage(messages['entry-actions.add-above']),
      onClick: () => handleInsertEntries(Position.BEFORE),
      closeOnClick: true,
      elemAfter: (
        <Inline xcss={elementAfterShortCutWrapper}>
          {tooltip(
            SELECT_ENTRY_SHORTCUTS[SELECT_ENTRY_MENU_ACTIONS.ADD_ENTRY_ABOVE]?.keyMap
          )}
        </Inline>
      ),
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <InsertRowAboveIcon label="" size="medium" />
        </Flex>
      ),
    }),
    [formatMessage, handleInsertEntries]
  );

  const addEntryBelowMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_ENTRY_MENU_ACTIONS.ADD_ENTRY_BELOW,
      content: formatMessage(messages['entry-actions.add-below']),
      onClick: () => handleInsertEntries(Position.AFTER),
      closeOnClick: true,
      elemAfter: (
        <Inline xcss={elementAfterShortCutWrapper}>
          {tooltip(
            SELECT_ENTRY_SHORTCUTS[SELECT_ENTRY_MENU_ACTIONS.ADD_ENTRY_BELOW]?.keyMap
          )}
        </Inline>
      ),
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <InsertRowBelowIcon label="" size="medium" />
        </Flex>
      ),
    }),
    [formatMessage, handleInsertEntries]
  );

  const duplicateEntriesMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_ENTRY_MENU_ACTIONS.DUPLICATE_ENTRIES,
      content: formatMessage(messages['entry-actions.duplicate-entry'], {
        entryCount: selectedEntriesCount,
      }),
      onClick: handleDuplicateEntries,
      closeOnClick: true,
      elemAfter: (
        <Inline xcss={elementAfterShortCutWrapper}>
          {tooltip(
            SELECT_ENTRY_SHORTCUTS[SELECT_ENTRY_MENU_ACTIONS.DUPLICATE_ENTRIES]?.keyMap
          )}
        </Inline>
      ),
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <CopyIcon label="" size="medium" />
        </Flex>
      ),
    }),
    [formatMessage, handleDuplicateEntries, selectedEntriesCount]
  );

  const clearCellsMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_ENTRY_MENU_ACTIONS.CLEAR_CELLS,
      content: formatMessage(messages['value-actions.clear-cells'], { valuesCount: 2 }),
      onClick: handleClearEntries,
      closeOnClick: true,
      elemAfter: (
        <Inline xcss={elementAfterShortCutWrapper}>
          {tooltip(SELECT_ENTRY_SHORTCUTS[SELECT_ENTRY_MENU_ACTIONS.CLEAR_CELLS]?.keyMap)}
        </Inline>
      ),
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <CrossCircleIcon label="" size="medium" />
        </Flex>
      ),
    }),
    [formatMessage, handleClearEntries]
  );

  const onHoverDeleteFieldButton = useCallback(() => {
    dispatch(setHoverDeleteButton({ value: 'entries' }));
  }, [dispatch]);

  const onLeaveDeleteFieldButton = useCallback(() => {
    dispatch(setHoverDeleteButton({ value: null }));
  }, [dispatch]);

  const deleteEntriesMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_ENTRY_MENU_ACTIONS.DELETE_ENTRIES,
      content: formatMessage(messages['entry-actions.delete'], {
        entryCount: selectedEntriesCount,
      }),
      onClick: handleDeleteEntries,
      onMouseEnter: onHoverDeleteFieldButton,
      onMouseLeave: onLeaveDeleteFieldButton,
      closeOnClick: true,
      elemAfter: (
        <Inline xcss={elementAfterShortCutWrapper}>
          {tooltip(
            SELECT_ENTRY_SHORTCUTS[SELECT_ENTRY_MENU_ACTIONS.DELETE_ENTRIES]?.keyMap
          )}
        </Inline>
      ),
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <TrashIcon
            label=""
            size="medium"
            primaryColor={
              isHoverDeleteEntriesButton
                ? token('color.icon.danger', colors.R500)
                : undefined
            }
          />
        </Flex>
      ),
    }),
    [
      formatMessage,
      handleDeleteEntries,
      isHoverDeleteEntriesButton,
      onHoverDeleteFieldButton,
      onLeaveDeleteFieldButton,
      selectedEntriesCount,
    ]
  );

  const moveEntriesUpMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_ENTRY_MENU_ACTIONS.MOVE_ENTRIES_UP,
      content: formatMessage(messages['entry-actions.move-up'], {
        entryCount: selectedEntriesCount,
      }),
      onClick: () => handleMoveEntries(Position.BEFORE),
      closeOnClick: true,
      elemAfter: (
        <Inline xcss={elementAfterShortCutWrapper}>
          {tooltip(
            SELECT_ENTRY_SHORTCUTS[SELECT_ENTRY_MENU_ACTIONS.MOVE_ENTRIES_UP]?.keyMap
          )}
        </Inline>
      ),
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <ArrowUpIcon label="" size="medium" />
        </Flex>
      ),
    }),
    [formatMessage, handleMoveEntries, selectedEntriesCount]
  );

  const moveEntriesDownMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_ENTRY_MENU_ACTIONS.MOVE_ENTRIES_DOWN,
      content: formatMessage(messages['entry-actions.move-down'], {
        entryCount: selectedEntriesCount,
      }),
      onClick: () => handleMoveEntries(Position.AFTER),
      closeOnClick: true,
      elemAfter: (
        <Inline xcss={elementAfterShortCutWrapper}>
          {tooltip(
            SELECT_ENTRY_SHORTCUTS[SELECT_ENTRY_MENU_ACTIONS.MOVE_ENTRIES_DOWN]?.keyMap
          )}
        </Inline>
      ),
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <ArrowDownIcon label="" size="medium" />
        </Flex>
      ),
    }),
    [formatMessage, handleMoveEntries, selectedEntriesCount]
  );
  const menuItems = useMemo(() => {
    const isEntryPanelActive = isPanelVisible('EntryViewPanel');
    const firstGroup: MenuItem[] = [];
    const secondGroup: MenuItem[] = [];
    const thirdGroup: MenuItem[] = [];
    if (oneSelectedEntry && !isEntryPanelActive) {
      firstGroup.push(expandEntryMenuItem);
    }
    if (oneSelectedEntry && isEntryPanelActive) {
      firstGroup.push(collapseEntryMenuItem);
    }
    if (canCopyValue) {
      firstGroup.push(copyEntryLinkMenuItem);
    }
    if (canInsertEntry) {
      secondGroup.push(addEntryAboveMenuItem);
      secondGroup.push(addEntryBelowMenuItem);
    }
    if (canDuplicateEntry) {
      secondGroup.push(duplicateEntriesMenuItem);
    }
    if (canDeleteEntry) {
      secondGroup.push(clearCellsMenuItem);
      secondGroup.push(deleteEntriesMenuItem);
    }
    if (canMoveUp) {
      thirdGroup.push(moveEntriesUpMenuItem);
    }
    if (canMoveDown) {
      thirdGroup.push(moveEntriesDownMenuItem);
    }
    return [{ items: firstGroup }, { items: secondGroup }, { items: thirdGroup }];
  }, [
    canMoveDown,
    canMoveUp,
    moveEntriesDownMenuItem,
    moveEntriesUpMenuItem,
    addEntryAboveMenuItem,
    addEntryBelowMenuItem,
    canCopyValue,
    canDeleteEntry,
    canDuplicateEntry,
    canInsertEntry,
    clearCellsMenuItem,
    collapseEntryMenuItem,
    copyEntryLinkMenuItem,
    deleteEntriesMenuItem,
    duplicateEntriesMenuItem,
    expandEntryMenuItem,
    isPanelVisible,
    oneSelectedEntry,
  ]);

  useKeyboardShortcut(
    isMenuOpen && canInsertEntry
      ? SELECT_ENTRY_SHORTCUTS[SELECT_ENTRY_MENU_ACTIONS.ADD_ENTRY_ABOVE]
          ?.keyboardShortCut
      : undefined,
    () => handleInsertEntries(Position.BEFORE)
  );
  useKeyboardShortcut(
    isMenuOpen && canInsertEntry
      ? SELECT_ENTRY_SHORTCUTS[SELECT_ENTRY_MENU_ACTIONS.ADD_ENTRY_BELOW]
          ?.keyboardShortCut
      : undefined,
    () => handleInsertEntries(Position.AFTER)
  );
  useKeyboardShortcut(
    isMenuOpen && canDuplicateEntry
      ? SELECT_ENTRY_SHORTCUTS[SELECT_ENTRY_MENU_ACTIONS.DUPLICATE_ENTRIES]
          ?.keyboardShortCut
      : undefined,
    handleDuplicateEntries
  );
  useKeyboardShortcut(
    isMenuOpen && canDeleteEntry
      ? SELECT_ENTRY_SHORTCUTS[SELECT_ENTRY_MENU_ACTIONS.CLEAR_CELLS]?.keyboardShortCut
      : undefined,
    handleClearEntries
  );
  useKeyboardShortcut(
    isMenuOpen && canDeleteEntry
      ? SELECT_ENTRY_SHORTCUTS[SELECT_ENTRY_MENU_ACTIONS.DELETE_ENTRIES]?.keyboardShortCut
      : undefined,
    handleDeleteEntries
  );
  useKeyboardShortcut(
    isMenuOpen && canMoveUp
      ? SELECT_ENTRY_SHORTCUTS[SELECT_ENTRY_MENU_ACTIONS.MOVE_ENTRIES_UP]
          ?.keyboardShortCut
      : undefined,
    () => handleMoveEntries(Position.BEFORE)
  );
  useKeyboardShortcut(
    isMenuOpen && canMoveDown
      ? SELECT_ENTRY_SHORTCUTS[SELECT_ENTRY_MENU_ACTIONS.MOVE_ENTRIES_DOWN]
          ?.keyboardShortCut
      : undefined,
    () => handleMoveEntries(Position.AFTER)
  );

  return <ContextualMenu menuData={menuData} items={menuItems} trigger={triggerComp} />;
};

export default SelectEntryMenu;
